import {useEffect, useRef, useState, type ReactNode} from 'react'
import {useTranslation} from 'react-i18next'
import type {TFunction} from 'i18next'
import {Button} from '@/components/ui/button'
import {Input} from '@/components/ui/input'
import {Tag} from '@/components/ui/tag'
import {AmberDot} from '@/components/amber-dot'
import {
  AtajoNumero,
  CropImage,
  MarcaDelSistema,
  OPCION_BASE,
  OPCION_OFF,
  OPCION_ON,
  OPCION_PROPUESTA,
  OPCION_PROPUESTA_TENUE,
} from '@/components/bateria/tira-opciones'
import {ChipDeEstado} from '@/components/bateria/cola/chip-estado'
import {VerElPapel} from '@/components/bateria/cola/ver-el-papel'
import {PaginaDelPapel} from '@/components/bateria/cola/pagina-del-papel'
import {IndicadorDeTransito} from '@/components/bateria/cola/evidencia-de-la-tarjeta'
import {NavegacionTarjeta} from '@/components/bateria/cola/posicion-tarjeta'
import {AvisoGesto, type GestoEnCurso} from '@/components/bateria/cola/gesto-en-curso'
import {HelpIcon} from '@/components/help-icon'
import {cn} from '@/lib/utils'
import {useRecorteVivo} from '@/lib/recortes-caidos'
import {
  ANTIGUEDAD_ANIOS,
  claveDeRef,
  esNumeroDeAnios,
  normalizarAnios,
  type GestoGuardado,
  type TarjetaCola,
} from '@/lib/bateria-cola'
import {tope} from '@/lib/campos-texto'

/**
 * `suelto`: el foco está en la tarjeta y las flechas navegan. `campo`: se entró con `Enter` y el valor
 * queda seleccionado. `campo-al-final`: se entró tecleando y ese carácter ya es el valor.
 */
export type FocoEscritura = 'suelto' | 'campo' | 'campo-al-final'

/** Sin él el baremo es incierto: su paquete queda bloqueado hasta resolverlo (G32). */
const CAMPO_QUE_DECIDE_EL_BAREMO = 'tipo_cargo'

function Encabezado({tarjeta, campo, posicion}: {tarjeta: TarjetaCola; campo: string; posicion: ReactNode}) {
  const {t} = useTranslation()
  return (
    <header className="surface-divider flex flex-wrap items-center gap-2 border-b px-5 py-3.5">
      <span className="font-mono text-[11px] tracking-wider text-muted-foreground uppercase">
        {t(`admin.bateria.cola.seccion.${tarjeta.seccion}`)}
      </span>
      <ChipDeEstado tarjeta={tarjeta} />
      {tarjeta.origen_valor && (
        <Tag data-testid="cola-campo-origen">
          {t(`admin.bateria.cola.origen.${tarjeta.origen_valor}`, {defaultValue: tarjeta.origen_valor})}
        </Tag>
      )}
      {tarjeta.alimenta_informe && <Tag tone="neutral">{t('admin.bateria.cola.campo.alimentaInforme')}</Tag>}
      {campo === CAMPO_QUE_DECIDE_EL_BAREMO && (
        <Tag tone="amber" data-testid="cola-campo-baremo">
          {t('admin.bateria.cola.campo.decideBaremo')}
        </Tag>
      )}
      <span className="ml-auto font-mono text-[11px] text-muted-foreground" data-testid="cola-campo-pie">
        {[tarjeta.persona_corta, t(`admin.bateria.ficha.campo.${campo}`, {defaultValue: campo})]
          .filter(Boolean)
          .join(' · ')}
      </span>
      {posicion}
    </header>
  )
}

/** Solo texto, nunca un formulario del instrumento (Res. 2764/2022). Sin enunciado en el catálogo no se pinta. */
function EnunciadoImpreso({enunciado}: {enunciado: string | null}) {
  if (!enunciado) return null
  return (
    <p className="text-sm text-foreground" data-testid="cola-campo-enunciado">
      {enunciado}
    </p>
  )
}

/**
 * El recorte calibrado si existe (vivo, `useRecorteVivo`: el lifecycle los borra a los 30 días) y si no,
 * la hoja del escaneo donde está impresa la pregunta.
 */
function Papel({tarjeta, businessId, evaluacionId}: {tarjeta: TarjetaCola} & Contexto) {
  const {t} = useTranslation()
  const recorte = useRecorteVivo(tarjeta.crop_download_url)
  return (
    // `relative` ancla `IndicadorDeTransito` en la esquina del papel.
    <div className="relative">
      <IndicadorDeTransito />
      {recorte ? (
        <CropImage
          url={recorte}
          alt={t('admin.bateria.ficha.recorteAlt')}
          fallback={t('admin.bateria.ficha.sinRecorte')}
          testId="cola-campo-recorte"
        />
      ) : (
        <PaginaDelPapel tarjeta={tarjeta} businessId={businessId} evaluacionId={evaluacionId} />
      )}
    </div>
  )
}

/** La hoja no viaja en la tarjeta (rasterizar es caro): la pide quien la muestra. */
type Contexto = {businessId: string; evaluacionId: string}

function ChipsDeOpciones({
  campo,
  opciones,
  seleccion,
  propuesta,
  senaladas,
  puedeDecidir,
  onElegir,
}: {
  campo: string
  opciones: string[]
  seleccion: string | null
  propuesta: string | null
  senaladas: readonly string[]
  puedeDecidir: boolean
  onElegir: (valor: string) => void
}) {
  const {t} = useTranslation()
  return (
    <div className="flex flex-wrap gap-1.5" data-testid="cola-confirmar-chips">
      {opciones.map((opcion, i) => {
        const activa = seleccion === opcion
        const esPropuesta = propuesta === opcion && !activa && !senaladas.includes(opcion)
        const propuestaViva = esPropuesta && seleccion === null
        const senalada = senaladas.includes(opcion)
        return (
          <button
            key={opcion}
            type="button"
            role="radio"
            aria-checked={activa}
            aria-keyshortcuts={i < 9 ? String(i + 1) : undefined}
            data-testid={`cola-confirmar-chip-${opcion}`}
            data-senalada={senalada ? 'true' : undefined}
            data-propuesta={propuestaViva ? 'true' : esPropuesta ? 'tenue' : undefined}
            disabled={!puedeDecidir}
            onClick={() => onElegir(opcion)}
            className={cn(
              OPCION_BASE,
              'inline-flex min-h-11 items-center gap-1 disabled:cursor-default md:min-h-0',
              activa ? OPCION_ON : OPCION_OFF,
              senalada && 'border-tone-amber-fg',
              propuestaViva && OPCION_PROPUESTA,
              esPropuesta && !propuestaViva && OPCION_PROPUESTA_TENUE
            )}>
            <AtajoNumero n={i + 1} />
            {senalada && <AmberDot label={t('common.senal.senalada')} testId={`cola-confirmar-senalada-${opcion}`} />}
            {propuestaViva && (
              <MarcaDelSistema
                etiqueta={t('admin.bateria.cola.campo.propuestaLeida')}
                testId={`cola-confirmar-propuesta-${opcion}`}
              />
            )}
            {t(`admin.bateria.ficha.opcion.${campo}.${opcion}`, {defaultValue: opcion})}
          </button>
        )
      })}
    </div>
  )
}

function ChipSinDato({activo, puedeDecidir, onElegir}: {activo: boolean; puedeDecidir: boolean; onElegir: () => void}) {
  const {t} = useTranslation()
  return (
    <button
      type="button"
      role="radio"
      aria-checked={activo}
      aria-keyshortcuts="0"
      data-testid="cola-confirmar-sin-dato"
      disabled={!puedeDecidir}
      onClick={onElegir}
      className={cn(
        OPCION_BASE,
        'min-h-11 border-dashed disabled:cursor-default md:min-h-0',
        activo ? OPCION_ON : OPCION_OFF
      )}>
      {t('admin.bateria.cola.sinDato')}
    </button>
  )
}

function AyudaDeGestos() {
  const {t} = useTranslation()
  return (
    <p
      className="flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-muted-foreground"
      data-testid="cola-confirmar-ayuda">
      <span aria-hidden="true" className="font-mono opacity-60">
        T
      </span>
      {t('admin.bateria.cola.campo.ayuda.tachon')}
      <span aria-hidden="true" className="font-mono opacity-60">
        M
      </span>
      {t('admin.bateria.cola.campo.ayuda.varias')}
    </p>
  )
}

/** Tachón sin respuesta y doble marcación guardan `valor: null`: sin esto se verían sin contestar. */
function GestoGuardadoDicho({gesto}: {gesto: GestoGuardado}) {
  const {t} = useTranslation()
  if (!gesto) return null
  return (
    <p className="font-mono text-[11px] text-muted-foreground" data-testid="cola-confirmar-gesto-guardado">
      {t(`admin.bateria.cola.campo.guardado.${gesto}`)}
    </p>
  )
}

type Gestos = {
  gesto: GestoEnCurso
  gestoGuardado: GestoGuardado
  /** Se repintan al volver: sin ellas el gesto guardado parecía perdido. */
  casillasGuardadas: readonly string[]
  onSenalar: (valor: string) => void
  /** Con un gesto en su mitad de señalar, el clic señala igual que el teclado. */
  clicSenala: boolean
}

/** Cáscara común de las tarjetas de casilla (ficha y antigüedad); solo cambia cómo se lee una respuesta. */
function TarjetaDeCasillas({
  tarjeta,
  campo,
  testId,
  posicion,
  seleccion,
  gestos,
  etiqueta,
  puedeDecidir,
  onElegir,
  onDespues,
  onAtras,
  onAdelante,
  businessId,
  evaluacionId,
  children,
}: {
  tarjeta: TarjetaCola
  campo: string
  testId: string
  posicion: ReactNode
  seleccion: string | null
  gestos: Gestos
  etiqueta: (valor: string) => string
  puedeDecidir: boolean
  onElegir: (valor: string | null) => void
  onDespues: () => void
  onAtras: () => void
  onAdelante: () => void
  children: ReactNode
} & Contexto) {
  const {t} = useTranslation()
  const propuesta = tarjeta.valor_propuesto
  const {gesto, gestoGuardado} = gestos

  return (
    <section className="surface" data-testid={testId}>
      <Encabezado tarjeta={tarjeta} campo={campo} posicion={posicion} />
      <div className="space-y-3 px-5 py-4">
        <EnunciadoImpreso enunciado={tarjeta.enunciado} />
        <Papel tarjeta={tarjeta} businessId={businessId} evaluacionId={evaluacionId} />
        {/* Pegado al papel: si la hoja no se rasterizó o quedó fuera del encuadre, la salida va justo debajo. */}
        <VerElPapel tarjeta={tarjeta} testIdPrefix="cola-campo" />
        {propuesta !== null && seleccion === null && (
          <p className="flex items-center gap-2 text-xs text-muted-foreground" data-testid="cola-campo-propuesta">
            <AmberDot label={t('common.senal.sinConfirmar')} />
            {t('admin.bateria.cola.campo.propuesta', {valor: etiqueta(propuesta)})}
            {tarjeta.confianza != null && (
              <span className="font-mono text-[11px] tabular-nums" data-testid="cola-campo-confianza">
                {t('admin.bateria.cola.marca.confianza', {pct: Math.round(tarjeta.confianza * 100)})}
              </span>
            )}
          </p>
        )}
        <div role="radiogroup" aria-label={t(`admin.bateria.ficha.campo.${campo}`, {defaultValue: campo})}>
          <div className="flex flex-wrap items-center gap-1.5">
            {children}
            <ChipSinDato activo={seleccion === ''} puedeDecidir={puedeDecidir} onElegir={() => onElegir(null)} />
          </div>
        </div>
        {gesto ? (
          <AvisoGesto
            testId="cola-confirmar-gesto"
            mensaje={t(
              gesto.tipo === 'tachon' && !gesto.senalando
                ? 'admin.bateria.cola.campo.modo.tachonValida'
                : `admin.bateria.cola.campo.modo.${gesto.tipo}`,
              {casillas: gesto.casillas.map(etiqueta).join(', ') || t('admin.bateria.cola.marca.ninguna')}
            )}
          />
        ) : (
          <AyudaDeGestos />
        )}
        <GestoGuardadoDicho gesto={gestoGuardado} />
      </div>
      <div className="surface-divider flex flex-wrap items-center justify-between gap-2 border-t px-5 py-3">
        <NavegacionTarjeta onAtras={onAtras} onAdelante={onAdelante} />
        {/* Con una selección hecha el primario confirma esa selección; solo sin nada elegido ofrece la propuesta. */}
        {(seleccion !== null || propuesta !== null) && (
          <Button
            size="sm"
            className="min-h-11 md:min-h-0"
            data-testid="cola-confirmar-aceptar"
            disabled={!puedeDecidir}
            onClick={() => onElegir(seleccion !== null ? seleccionComoValor(seleccion) : propuesta)}>
            {seleccion !== null
              ? t('admin.bateria.cola.campo.confirmarElegido', {valor: etiquetaDeSeleccion(seleccion, etiqueta, t)})
              : t('admin.bateria.cola.campo.aceptar', {valor: etiqueta(propuesta!)})}
          </Button>
        )}
        <Button
          size="sm"
          variant="ghost"
          className="min-h-11 md:min-h-0"
          data-testid="cola-despues"
          onClick={onDespues}>
          {t('admin.bateria.cola.despues')}
        </Button>
      </div>
    </section>
  )
}

/** `''` es el chip «acá no hay dato» y viaja como `null`: la cadena vacía guardaría un texto en blanco. */
function seleccionComoValor(seleccion: string): string | null {
  return seleccion === '' ? null : seleccion
}

function etiquetaDeSeleccion(seleccion: string, etiqueta: (valor: string) => string, t: TFunction): string {
  return seleccion === '' ? t('admin.bateria.cola.sinDato') : etiqueta(seleccion)
}

type PropsDeCasillas = {
  tarjeta: TarjetaCola
  opciones: string[]
  posicion: ReactNode
  seleccion: string | null
  gestos: Gestos
  /** `tarjetaContestable` y la evidencia pintada, calculado una vez por la pantalla (`evidencia-de-la-tarjeta.tsx`). */
  puedeDecidir: boolean
  onElegir: (valor: string | null) => void
  onDespues: () => void
  onAtras: () => void
  onAdelante: () => void
} & Contexto

/**
 * v2 §1.4. A diferencia de las marcas, `Enter` acepta la propuesta: la bandera de ficha casi nunca
 * dice «no vi», dice «otro lector no coincidió».
 */
export function TarjetaConfirmar({tarjeta, opciones, gestos, onElegir, ...resto}: PropsDeCasillas) {
  const {t} = useTranslation()
  const campo = claveDeRef(tarjeta.ref) ?? tarjeta.ref
  const etiqueta = (valor: string) => t(`admin.bateria.ficha.opcion.${campo}.${valor}`, {defaultValue: valor})

  return (
    <TarjetaDeCasillas
      tarjeta={tarjeta}
      campo={campo}
      testId="cola-tarjeta-confirmar"
      gestos={gestos}
      etiqueta={etiqueta}
      onElegir={onElegir}
      {...resto}>
      <ChipsDeOpciones
        campo={campo}
        opciones={opciones}
        seleccion={resto.seleccion}
        propuesta={tarjeta.valor_propuesto}
        senaladas={gestos.gesto?.casillas ?? gestos.casillasGuardadas}
        puedeDecidir={resto.puedeDecidir}
        onElegir={(opcion) => (gestos.clicSenala ? gestos.onSenalar(opcion) : onElegir(opcion))}
      />
    </TarjetaDeCasillas>
  )
}

/** La pregunta de antigüedad entera: por columnas, el `<input>` de años no podía representar «menos de un año». */
export function TarjetaAntiguedad({
  tarjeta,
  opciones,
  gestos,
  onElegir,
  anios,
  foco,
  onEscribirAnios,
  onCambiarAnios,
  onConfirmarAnios,
  onSoltarFoco,
  ...resto
}: PropsDeCasillas & {
  anios: string
  foco: FocoEscritura
  onEscribirAnios: () => void
  onCambiarAnios: (valor: string) => void
  onConfirmarAnios: () => void
  onSoltarFoco: () => void
}) {
  const {t} = useTranslation()
  const campo = claveDeRef(tarjeta.ref) ?? tarjeta.ref
  const etiqueta = (valor: string) => etiquetaDeAntiguedad(t, valor)
  const input = useRef<HTMLInputElement>(null)
  // El error vive acá y no como envío fallido: `registrar` avanza la cola de inmediato y el rechazo
  // del servidor llegaría con otra tarjeta a la vista.
  const [errorAnios, setErrorAnios] = useState<string | null>(null)
  useEffect(() => setErrorAnios(null), [tarjeta.ref])

  useEffect(() => {
    if (foco !== 'suelto') input.current?.focus()
  }, [foco, tarjeta.ref])

  function intentarConfirmarAnios() {
    if (anios.trim() !== '' && normalizarAnios(anios) === null) {
      setErrorAnios(t('admin.bateria.cola.campo.antiguedad.errorAnios'))
      return
    }
    setErrorAnios(null)
    onConfirmarAnios()
  }

  return (
    <TarjetaDeCasillas
      tarjeta={tarjeta}
      campo={campo}
      testId="cola-tarjeta-antiguedad"
      gestos={gestos}
      etiqueta={etiqueta}
      onElegir={onElegir}
      {...resto}>
      {opciones.map((opcion, i) => {
        const esAnios = opcion === ANTIGUEDAD_ANIOS
        const contestadaConAnios = esNumeroDeAnios(resto.seleccion)
        const activa = esAnios ? contestadaConAnios : resto.seleccion === opcion
        const senalada = (gestos.gesto?.casillas ?? gestos.casillasGuardadas).includes(opcion)
        // `opcion` aquí es el sentinela `anios`: solo con el número elegido se lee «12 años».
        const rotulo = esAnios && contestadaConAnios ? etiqueta(resto.seleccion!) : etiqueta(opcion)
        return (
          <button
            key={opcion}
            type="button"
            role="radio"
            aria-checked={activa}
            aria-keyshortcuts={String(i + 1)}
            data-testid={`cola-antiguedad-opcion-${opcion}`}
            data-senalada={senalada ? 'true' : undefined}
            disabled={!resto.puedeDecidir}
            onClick={() => {
              if (gestos.clicSenala) gestos.onSenalar(opcion)
              else if (esAnios) onEscribirAnios()
              else onElegir(opcion)
            }}
            className={cn(
              OPCION_BASE,
              'inline-flex min-h-11 items-center gap-1 disabled:cursor-default md:min-h-0',
              activa ? OPCION_ON : OPCION_OFF,
              senalada && 'border-tone-amber-fg'
            )}>
            <AtajoNumero n={i + 1} />
            {senalada && <AmberDot label={t('common.senal.senalada')} testId={`cola-antiguedad-senalada-${opcion}`} />}
            {rotulo}
          </button>
        )
      })}
      <label className="flex items-center gap-2">
        <span className="font-mono text-[11px] tracking-wider text-muted-foreground uppercase">
          {t('admin.bateria.cola.campo.antiguedad.cuantos')}
        </span>
        <Input
          maxLength={tope('ANIO')}
          ref={input}
          // `text` y no `number`: el input numérico nativo descarta la coma al teclearla; `inputMode` da el teclado.
          type="text"
          inputMode="decimal"
          data-testid="cola-antiguedad-anios"
          // Sin papel tampoco se teclea: el número sería la puerta a la marca falsa que el interlock impide.
          disabled={!resto.puedeDecidir}
          value={anios}
          onChange={(e) => {
            // Se normaliza al teclear: lo que queda en el borrador ya es lo que el servidor entiende.
            onCambiarAnios(e.target.value.replace(',', '.'))
            if (errorAnios) setErrorAnios(null)
          }}
          onBlur={onSoltarFoco}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault()
              intentarConfirmarAnios()
            }
            if (e.key === 'Escape') e.currentTarget.blur()
          }}
          aria-invalid={errorAnios !== null}
          className="max-w-24 font-mono"
        />
      </label>
      {errorAnios && (
        <p role="alert" className="text-xs text-destructive" data-testid="cola-antiguedad-error-anios">
          {errorAnios}
        </p>
      )}
    </TarjetaDeCasillas>
  )
}

function etiquetaDeAntiguedad(t: TFunction, valor: string): string {
  if (esNumeroDeAnios(valor)) return t('admin.bateria.cola.campo.antiguedad.anios', {count: Number(valor)})
  return t(`admin.bateria.cola.campo.antiguedad.${valor}`, {defaultValue: valor})
}

/**
 * v2 §1.4. La lectura del OCR entra como valor, no como placeholder: `Enter` confirma y escribir reemplaza.
 * Se llega con el foco suelto (`FocoEscritura`); `onBlur` reporta el clic afuera.
 */
export function TarjetaEscribir({
  tarjeta,
  valor,
  posicion,
  foco,
  puedeDecidir,
  onCambiar,
  onConfirmar,
  onSoltarFoco,
  onDespues,
  onAtras,
  onAdelante,
  businessId,
  evaluacionId,
}: {
  tarjeta: TarjetaCola
  valor: string
  posicion: ReactNode
  foco: FocoEscritura
  puedeDecidir: boolean
  onCambiar: (valor: string) => void
  onConfirmar: () => void
  onSoltarFoco: () => void
  onDespues: () => void
  onAtras: () => void
  onAdelante: () => void
} & Contexto) {
  const {t} = useTranslation()
  const campo = claveDeRef(tarjeta.ref) ?? tarjeta.ref
  const input = useRef<HTMLInputElement>(null)
  // El punteado dice «nadie tocó esto», no «vino del OCR»: se apaga con la primera pulsación.
  const [tocado, setTocado] = useState(false)
  useEffect(() => setTocado(false), [tarjeta.ref])
  const esPropuestaSinTocar = tarjeta.valor_propuesto !== null && !tocado && valor === tarjeta.valor_propuesto

  useEffect(() => {
    const campoDom = input.current
    if (foco === 'suelto' || campoDom === null) return
    campoDom.focus()
    // Solo al entrar con `Enter`: si se entró tecleando, seleccionar haría que el siguiente carácter borre el primero.
    if (foco === 'campo') campoDom.select()
    else campoDom.setSelectionRange(campoDom.value.length, campoDom.value.length)
  }, [foco, tarjeta.ref])

  return (
    <section className="surface" data-testid="cola-tarjeta-escribir">
      <Encabezado tarjeta={tarjeta} campo={campo} posicion={posicion} />
      <div className="space-y-3 px-5 py-4">
        <EnunciadoImpreso enunciado={tarjeta.enunciado} />
        <Papel tarjeta={tarjeta} businessId={businessId} evaluacionId={evaluacionId} />
        <VerElPapel tarjeta={tarjeta} testIdPrefix="cola-campo" />
        {tarjeta.valor_propuesto !== null && tarjeta.origen_valor !== null && (
          <p className="flex items-center gap-2 text-xs text-muted-foreground" data-testid="cola-escribir-origen">
            <AmberDot label={t('common.senal.sinConfirmar')} />
            {t(`admin.bateria.cola.origen.${tarjeta.origen_valor}`, {defaultValue: tarjeta.origen_valor})}
            {tarjeta.confianza != null && (
              <span className="font-mono text-[11px] tabular-nums" data-testid="cola-campo-confianza">
                {t('admin.bateria.cola.marca.confianza', {pct: Math.round(tarjeta.confianza * 100)})}
              </span>
            )}
          </p>
        )}
        <label className="block space-y-1.5">
          <span className="inline-flex items-center gap-1.5 font-mono text-[11px] tracking-wider text-muted-foreground uppercase">
            {t(`admin.bateria.ficha.campo.${campo}`, {defaultValue: campo})}
            <HelpIcon
              label={t('common.help.iconLabelFor', {
                campo: t(`admin.bateria.ficha.campo.${campo}`, {defaultValue: campo}),
              })}
              testId="cola-escribir-ayuda">
              {t('admin.bateria.cola.campo.tip')}
            </HelpIcon>
          </span>
          <Input
            maxLength={tope('NOTA')}
            ref={input}
            data-testid="cola-escribir-valor"
            disabled={!puedeDecidir}
            value={valor}
            onChange={(e) => {
              onCambiar(e.target.value)
              setTocado(true)
            }}
            onBlur={onSoltarFoco}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault()
                onConfirmar()
              }
              if (e.key === 'Escape') e.currentTarget.blur()
            }}
            className={cn('font-mono', esPropuestaSinTocar && 'border-dashed border-tone-amber-fg')}
          />
        </label>
      </div>
      <div className="surface-divider flex flex-wrap items-center justify-between gap-2 border-t px-5 py-3">
        <NavegacionTarjeta onAtras={onAtras} onAdelante={onAdelante} />
        <Button
          size="sm"
          className="min-h-11 md:min-h-0"
          data-testid="cola-escribir-confirmar"
          disabled={!puedeDecidir}
          onClick={onConfirmar}>
          {t('admin.bateria.cola.campo.confirmar')}
        </Button>
        <Button
          size="sm"
          variant="ghost"
          className="min-h-11 md:min-h-0"
          data-testid="cola-despues"
          onClick={onDespues}>
          {t('admin.bateria.cola.despues')}
        </Button>
      </div>
    </section>
  )
}
